import {
  Country,
  ParcelStatus,
  Shop,
  Theme,
  TransportationMode,
} from "@/enums";
import React, { FC, useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import {
  renderCountryImage,
  renderShopIcon,
  renderTransportationIcon,
} from "@/utils/iconRenderer";
import Image from "next/image";

import PredeliveryAddress from "./predelivery-address";
import PredeliveryPayType from "./predelivery-paytype";

import { displayTransportationMode } from "../../get-parcels-table/components/transportation-wrapper";
import { Category } from "../../get-parcels-table/components/parcel-category";
import { ICurrentShipment, IShipmentInvoice } from "@/types/apiResponse.types";
import { useGetShipmentTypesRequest } from "@/api/useGetShipmentTypesRequest";
import { useGetAllFromCountriesRequest } from "@/api/useGetAllFromCountriesRequest";
import { nationalDeliveryPayTypeOptions } from "@/components/common/select/national-delivery-pay-type-select/select-national-delivery-pay-type-flow";
import { deliveredStatusses } from "@/app/(protected)/parcels/get-parcel/arrived/page";
import { openInvoiceFile } from "@/utils/downloadInvoice";
import { useTheme } from "@/context/Theme.context";
import { isGroundGeo } from "@/utils/customUtils";
import { formatNumber } from "@/utils/formatNumber";

type TProps = {
  currentParcel: ICurrentShipment;
  parcelStatus: ParcelStatus;
  invoiceFile: IShipmentInvoice | null;
};

const editParcelStatusses = [
  ParcelStatus.COMMON_STATUS_PROCESSED_AT_THE_INSTOCK,
  ParcelStatus.COMMON_STATUS_PREPARED_SHIPPING_FROM_FCOUNTRY,
  ParcelStatus.COMMON_STATUS_SHIPPED_FROM_FCOUNTRY,
  ParcelStatus.COMMON_STATUS_RECEIVED_OUTSTOCK,
  ParcelStatus.COMMON_STATUS_READY_FOR_PICKUP,
];

const ViewParcelDetails: FC<TProps> = ({
  currentParcel,
  parcelStatus,
  invoiceFile,
}) => {
  const t = useTranslations();
  const theme = useTheme();

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingPaymentType, setIsEditingPaymentType] = useState(false);

  const { data: productCategoriesData } = useGetShipmentTypesRequest();
  const { data: countriesData } = useGetAllFromCountriesRequest();

  const productCategories = productCategoriesData?.data?.data || [];
  const countries = countriesData?.data?.data || [];
  const shipmentCountry = countries.find(
    (item) => item.countryId === currentParcel?.fromCountryId,
  );

  const isPreDeliveryAddressEditable =
    editParcelStatusses.includes(parcelStatus);

  const handleOpenEditAddress = () => {
    setIsEditingAddress(true);
  };

  const handleCloseEditAddress = () => {
    setIsEditingAddress(false);
  };

  const handleOpenEditPaymentType = () => {
    setIsEditingPaymentType(true);
  };

  const handleCloseEditPaymentType = () => {
    setIsEditingPaymentType(false);
  };

  const parcelDetails = useMemo(() => {
    if (!currentParcel) return;

    // custom transportation type on GEORGIA
    const groundGeo = isGroundGeo(theme as Theme, currentParcel?.fromCountryId);

    const constFieldName = theme === Theme.UZ ? "costNational" : "costUsd";

    return [
      {
        label: "tracking_number",
        value: (
          <p className="text-[#808080]">
            {currentParcel?.trackCode || t("no_tracking_code")}
          </p>
        ),
      },
      {
        label: "country_of_delivery",
        value: (
          <div className="flex items-center gap-5">
            <div className="h-8 w-8">
              <Image
                src={renderCountryImage[currentParcel?.fromCountryId]}
                width={100}
                height={100}
                alt="country-image"
              />
            </div>
            <p className="text-medium uppercase text-black">
              {shipmentCountry?.countryName || ""}
            </p>
          </div>
        ),
      },
      {
        label: "web_shop_address",
        value: (
          <div className="flex items-center gap-5">
            {currentParcel?.onlineShop && (
              <>
                <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#f9f9f9] p-1">
                  <Image
                    src={
                      renderShopIcon[currentParcel.onlineShop] ||
                      renderShopIcon[Shop.UNIVERSAL]
                    }
                    width={100}
                    height={100}
                    alt={currentParcel.onlineShop || "shop-image"}
                  />
                </div>
                <p className="text-medium text-black">
                  {currentParcel.onlineShop}
                </p>
              </>
            )}
          </div>
        ),
      },
      {
        label: "transportation_type",
        value: (
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f9f9f9]">
              {groundGeo
                ? renderTransportationIcon[TransportationMode.GROUND]
                : renderTransportationIcon[
                    currentParcel?.deliveryFromFcountryTypeId
                  ]}{" "}
            </span>
            <span className="text-sm font-medium text-black">
              {groundGeo
                ? displayTransportationMode(TransportationMode.GROUND, t)
                : displayTransportationMode(
                    currentParcel?.deliveryFromFcountryTypeId,
                    t,
                  )}
            </span>
            <span className="text-sm font-medium text-black">
              {`${theme === Theme.UZ ? "UZS " : "$ "} ${formatNumber(currentParcel?.[constFieldName] || 0)} `}
            </span>
          </div>
        ),
      },
      {
        label: "delivery_location",
        value: (
          <PredeliveryAddress
            isEditing={isEditingAddress}
            startEditing={handleOpenEditAddress}
            closeEditing={handleCloseEditAddress}
            shipmentId={currentParcel?.shipmentId}
            addressText={
              currentParcel?.deliveryAddressText ||
              currentParcel?.preDeliveryAddressText
            }
            addressId={
              currentParcel?.deliveryAddressId ||
              currentParcel?.addressIdPreDelivery
            }
            isPreDeliveryAddressEditable={isPreDeliveryAddressEditable}
          />
        ),
      },
      {
        label: "payment_method",
        value: (
          <PredeliveryPayType
            isEditing={isEditingPaymentType}
            startEditing={handleOpenEditPaymentType}
            closeEditing={handleCloseEditPaymentType}
            shipmentId={currentParcel?.shipmentId}
            paymentText={
              nationalDeliveryPayTypeOptions.find(
                (item) =>
                  item.id === currentParcel?.preNationalDeliveryPayTypeId,
              )?.mainText || ""
            }
            payTypeId={currentParcel?.preNationalDeliveryPayTypeId}
            isEditable={isPreDeliveryAddressEditable}
          />
        ),
      },
      {
        label: deliveredStatusses.includes(parcelStatus)
          ? "delivered"
          : "estimated_arrival",
        value: (
          <p className="text-[#808080]">
            {currentParcel?.receiveTcountryExpectedDate || ""}
          </p>
        ),
      },
      {
        label: "gross_weight",
        value: (
          <p className="text-[#808080]">{currentParcel?.weightKg || 0} KG</p>
        ),
      },
      {
        label: "transportation_price",
        value: (
          <p className="text-[#808080]">
            {theme === Theme.UZ ? t("uzs") : "$"}{" "}
            {formatNumber(currentParcel?.[constFieldName] || 0)}
          </p>
        ),
      },
    ];
  }, [currentParcel, isEditingAddress, isEditingPaymentType, theme]);

  return (
    <ul>
      {parcelDetails?.map(({ label, value }) => {
        if (label === "payment_method" && theme === Theme.GEORGIA) {
          return;
        }
        return (
          <li
            key={label}
            className="flex items-center justify-between border-b py-5"
          >
            <p className="text-sm font-semibold text-black">{t(label)}</p>
            {value}
          </li>
        );
      })}
      <li className="flex items-center justify-between border-b py-5">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-black">{t("description")}</p>
          <p className="text-sm font-medium text-[#808080]">
            {currentParcel?.shipmentProducts
              ?.map((product) => product?.productDescription)
              .join(", ")}
          </p>
        </div>
      </li>
      <li className="flex items-center justify-between border-b py-5">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-black">{t("invoice")}</p>
          {invoiceFile?.fileContentInBase64 && (
            <div
              className="cursor-pointer"
              onClick={() => openInvoiceFile(invoiceFile)}
            >
              <p className="text-sm font-medium text-[#808080]">
                Invoice.{invoiceFile?.fileExtension}
              </p>
            </div>
          )}
        </div>
      </li>
      <li className="flex items-center justify-start gap-[10px] border-b py-5">
        <Category
          category={currentParcel?.shipmentTypeId}
          shipmentTypeParentId={currentParcel?.shipmentTypeId}
        />
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-black">{t("category")}</p>
          <p className="text-sm font-medium text-[#808080]">
            {productCategories.find(
              (item) => item.shipmentTypeId === currentParcel?.shipmentTypeId,
            )?.shipmentTypeName || ""}
          </p>
        </div>
      </li>
      <li className="flex items-center justify-between border-b py-5">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-black">
            {t("product_price")}
          </p>
          <p className="text-sm font-medium text-[#808080]">
            $ {currentParcel?.declaredPriceUsd || 0}
          </p>
        </div>
      </li>
    </ul>
  );
};

export default ViewParcelDetails;
